/**
 * Nightholm bundle pricing, rule (b) chosen by Sami on 15 Oct 2026.
 * ONE source of truth: the product page, the Shopify discount function and the tests all use this file.
 *
 * Price classes (all money in cents):
 *   heated Standard      single 14995  pair 23995  four 41995   (repriced 20 Oct; pair/four set 21 Oct 2026)
 *   heated Large         single 16995  pair 26995  four 47595   (pair = one blanket at its compare-at price)
 *   fur Medium           single  9995  pair 15995  four 27995
 *   fur Large            single 12995  pair 20995  four 35995
 *   fur Small            single  8995  pair 13995  four 24995   (added 21 Oct 2026, Skills/offer-building.md)
 *   fur Large Wide       single 13995  pair 21995  four 38995   (added 21 Oct 2026, Skills/offer-building.md)
 *   fur XL               single 15995  pair 25995  four 44995
 *
 * Rules:
 *  - Bundles never mix products: a heated blanket and an Isla are priced separately (21 Oct 2026).
 *  - The blankets of one product are split into "four" groups, "pair" groups and singles in whichever way is CHEAPEST for
 *    the customer (exact search over every split, so the customer never has to think about the order they add things).
 *  - PAIR ("Buy one, get one free"): you pay the pair price of the dearer blanket and the cheaper one is free,
 *    capped so a pair never costs more than the two singles. (Heated + fur Medium = $279.90, no saving, because the cap bites.)
 *  - FOUR ("Buy two, get two free"): each blanket costs its own share of its class's four price (four price / 4),
 *    summed and rounded to the cent, capped at the singles. Four of one class therefore costs exactly that class's four price.
 *    The FOUR rule for mixed sets is my default (Sami chose (b) for pairs only); change it here if he decides otherwise.
 */
const CLASSES = {
  'heated-standard': { single: 14995, pair: 23995, four: 41995 },
  'heated-large': { single: 16995, pair: 26995, four: 47595 },
  'fur-small': { single: 8995, pair: 13995, four: 24995 },
  'fur-medium': { single: 9995, pair: 15995, four: 27995 },
  'fur-large': { single: 12995, pair: 20995, four: 35995 },
  'fur-largewide': { single: 13995, pair: 21995, four: 38995 },
  'fur-xl': { single: 15995, pair: 25995, four: 44995 },
};

/** classOf maps a product handle + Size option value to a price class. */
function classOf(handle, size) {
  if (handle === 'heated-blanket') return String(size || '').toLowerCase().startsWith('large') ? 'heated-large' : 'heated-standard';
  if (handle === 'fur-blanket') {
    const s = String(size || '').toLowerCase();
    if (s.startsWith('small')) return 'fur-small';
    if (s.startsWith('medium')) return 'fur-medium';
    if (s.startsWith('large wide')) return 'fur-largewide'; // before 'large', which it also starts with
    if (s.startsWith('large')) return 'fur-large';
    if (s.startsWith('xl')) return 'fur-xl';
  }
  return null; // not a bundle item
}

/**
 * price(units) -> { full, total, saving, groups }
 * units: array of class names, one entry per blanket (quantity expanded).
 */
function groupTotal(kind, cls) {
  const singles = cls.reduce((s, c) => s + CLASSES[c].single, 0);
  if (kind === 'single') return singles;
  if (kind === 'pair') { const dearer = cls[0]; return Math.min(CLASSES[dearer].pair, singles); }
  return Math.min(Math.round(cls.reduce((s, c) => s + CLASSES[c].four / 4, 0)), singles);
}
// Built on the first price() call, not when the file loads: the Shopify discount function loads this file but never
// calls price(), and its runtime counts every instruction (with six classes the eager build cost ~3.5M of the 11M limit).
let NAMES = null, PAIRS = null, FOURS = null;
function setup() {
  if (NAMES) return;
  NAMES = Object.keys(CLASSES).sort((a, b) => CLASSES[b].single - CLASSES[a].single || a.localeCompare(b)); // dearest first
  // every multiset of k classes, dearest first
  const combos = (k, start = 0) => { if (k === 0) return [[]]; const out = []; for (let i = start; i < NAMES.length; i++) for (const rest of combos(k - 1, i)) out.push([NAMES[i], ...rest]); return out; };
  PAIRS = combos(2); FOURS = combos(4);
}

// Bundles never cross products (Sami, 21 Oct 2026: "each product should be bought from its own page only"):
// the cart is priced product by product (class prefix "heated" / "fur") and the results are added.
function productOf(c) { return String(c).split('-')[0]; }
function price(units) {
  const byProduct = {};
  for (const c of units) (byProduct[productOf(c)] = byProduct[productOf(c)] || []).push(c);
  const parts = Object.keys(byProduct).map(k => priceOneProduct(byProduct[k]));
  const full = parts.reduce((s, r) => s + r.full, 0), total = parts.reduce((s, r) => s + r.total, 0);
  return { full, total, saving: full - total, groups: parts.flatMap(r => r.groups) };
}
function priceOneProduct(units) {
  setup();
  const counts = NAMES.map(n => units.filter(c => c === n).length);
  const memo = new Map();
  function best(cnt) {
    const key = cnt.join(',');
    if (memo.has(key)) return memo.get(key);
    if (cnt.every(x => x === 0)) { const r = { total: 0, groups: [] }; memo.set(key, r); return r; }
    let res = null;
    const tryGroup = (kind, cls) => {
      const next = cnt.slice();
      for (const c of cls) { const i = NAMES.indexOf(c); if (--next[i] < 0) return; }
      const sub = best(next); const t = groupTotal(kind, cls) + sub.total;
      if (!res || t < res.total) res = { total: t, groups: [{ kind, items: cls, singles: groupTotal('single', cls), total: groupTotal(kind, cls) }, ...sub.groups] };
    };
    for (const f of FOURS) tryGroup('four', f);
    for (const p of PAIRS) tryGroup('pair', p);
    for (const n of NAMES) tryGroup('single', [n]);
    memo.set(key, res); return res;
  }
  const r = best(counts);
  const full = units.filter(c => CLASSES[c]).reduce((s, c) => s + CLASSES[c].single, 0);
  return { full, total: r.total, saving: full - r.total, groups: r.groups };
}

if (typeof module !== 'undefined') module.exports = { CLASSES, classOf, price };
if (typeof window !== 'undefined') window.NHPricing = { CLASSES, classOf, price };
